import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import Toast from "../components/Toast";
import UploadProgress from "../components/UploadProgress";
import { UserContext } from "../context/UserContext";
import { getUserDevice } from "../helpers/Utils";
import { eventIdentify, rudderStackTrackEvent } from "../plugins/analytics";
import auth from "../services/auth";
import { checkFactoryIsKws } from "../services/kwsService";
import Beamsaw from "./Beamsaw/Beamsaw";
import styles from './Dashboard.module.css';
import PanelListView from "./QCcheck/PanelListView";
import QcCheck from "./QCcheck/QcCheck";
import QcSort from "./QCSort/QcSort";
import ImageSearch from "./ImageSearch";
import InvoiceManagementGate from "./InvoiceManagement/InvoiceManagementGate";
import InviteVendor from "./VendorOnboarding/InviteVendor";
import VendorProfilePage from "./VendorProfile/VendorProfilePage";
import FactorySelector from "../components/FactorySelector";
import SidebarOverlay from "../components/SidebarOverlay";
import { useIsMobile } from "../hooks/useIsMobile";
import { useMobileSidebarState } from "../hooks/useMobileSidebarState";
import {
  PermissionsProvider,
  loadPermissionsFromStorage,
  useStalePermissionsCacheGuard,
} from "../context/PermissionsContext";
import { TARS_MODULES } from "./InvoiceManagement/constants/modules";

const factoryStore = JSON.parse(localStorage.getItem('factory'));
const invoiceFactoryStore = JSON.parse(localStorage.getItem('invoiceFactory'));
const PRODUCTION_ROUTES = TARS_MODULES.filter((m) => m.production).map((m) => m.link);
const REDIRECT_URI = "/";

function Dashboard() {
  let navigate = useNavigate();
  const location = useLocation();
  const isInvoiceManagementPage = location.pathname.includes('/invoice-management');
  const isVendorOnboardingPage = location.pathname.includes('/vendor-onboarding');
  const isVendorProfilePage = location.pathname.includes('/vendor-profile');
  const skipFactoryGate = isInvoiceManagementPage || isVendorOnboardingPage || isVendorProfilePage;
  const cacheStale = useStalePermissionsCacheGuard();
  const bouncerPermissions = useMemo(() => loadPermissionsFromStorage(), []);
  const [user, setUser] = useState({});
  const [workOrder, setWorkOrder] = useState({});
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState({});
  const [toastList, setToastList] = useState([]);
  const [showFactory, setShowFactory] = useState(skipFactoryGate ? false : factoryStore ?  false : true);
  const [currentFactory, setCurrentFactory] = useState(factoryStore || {});
  const [invoiceFactory, setInvoiceFactory] = useState(invoiceFactoryStore || {});
  const [searchFocus, setSearchFocus] = useState(false);
  const [showLoadingFull, setShowLoadingFull] = useState(false);
  const [topHeader, setTopHeader] = useState({});
  const [uploadList, setUploadList] = useState([]);
  const [failedList, setFailedList] = useState({});
  const isMobile = useIsMobile();
  const [openMenu, setOpenMenu] = useMobileSidebarState(isMobile);

  // KWS vendor detection state
  // Note: isKwsFactory is a single flag shared by both currentFactory and invoiceFactory.
  // Switching invoice factory on the invoice page overwrites the KWS status set for the
  // production factory. Acceptable for now — vendors typically have the same KWS status
  // across their factories. Split into two flags if this assumption breaks.
  const [isInternal] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("TARS_IS_INTERNAL")) === true; }
    catch { return false; } // default to vendor (safe — redirect guard protects production routes)
  });
  const [isKwsFactory, setIsKwsFactory] = useState(() => {
    // Initialize from sessionStorage cache to avoid layout flash on refresh
    try {
      const cache = JSON.parse(sessionStorage.getItem("KWS_FACTORY_CACHE")) || {};
      const factoryCode = isInvoiceManagementPage
        ? (invoiceFactoryStore?.item_code || factoryStore?.item_code)
        : (factoryStore?.item_code || invoiceFactoryStore?.item_code);
      if (factoryCode === "all") return true; // tabs visible, Sidebar blocks navigation
      if (factoryCode && factoryCode in cache) return cache[factoryCode];
    } catch { /* ignore */ }
    return null; // null = unknown, true/false = resolved
  });
  const latestFactoryRef = useRef(null);
  const hasMountedRef = useRef(false);

  const runKwsCheck = useCallback(async (factoryCode) => {
    if (!factoryCode) return;
    latestFactoryRef.current = factoryCode;

    // "All factories" — keep production tabs visible but Sidebar blocks navigation
    if (factoryCode === "all") {
      setIsKwsFactory(true);
      return;
    }

    try {
      const isKws = await checkFactoryIsKws(factoryCode);
      if (latestFactoryRef.current !== factoryCode) return; // stale — user switched factory
      setIsKwsFactory(isKws);

      // Fetch a fresh TARS token when KWS factory is confirmed and no
      // in-memory token exists. We check in-memory (not sessionStorage) so that:
      // - Page refresh: in-memory is cleared → fetches fresh (sessionStorage may be stale)
      // - Tab switch within session: in-memory is set → skips redundant fetch
      if (isKws && !auth.userDetails?.tarsAccessToken) {
        try {
          const tokenResponse = await auth.getTarsAccessToken();
          if (latestFactoryRef.current !== factoryCode) return; // stale
          if (tokenResponse?.data) {
            auth.userDetails.tarsAccessToken = tokenResponse.data;
            sessionStorage.setItem("TARS_ACCESS_TOKEN", tokenResponse.data);
          }
        } catch (err) {
          console.error("Failed to fetch TARS token on-demand:", err);
          if (latestFactoryRef.current !== factoryCode) return; // stale
          // Without TARS token, production modules will fail — hide them
          setIsKwsFactory(false);
          return;
        }
      }
    } catch (err) {
      console.error("KWS check failed:", err);
      if (latestFactoryRef.current === factoryCode) {
        setIsKwsFactory(false); // safe default: hide production modules
      }
    }
  }, []);

  const contextData = useMemo(() => ({
    user,
    workOrder,
    setWorkOrder,
    availableLocations,
    setAvailableLocations,
    selectedLocation,
    setSelectedLocation,
    toastList,
    setToastList,
    currentFactory,
    setShowLoadingFull,
    topHeader,
    setTopHeader,
    uploadList,
    setUploadList,
    failedList,
    setFailedList,
    invoiceFactory,
    setInvoiceFactory,
    openMenu,
    setOpenMenu,
    isInternal,
    isKwsFactory,
  }), [
    user, workOrder, availableLocations, selectedLocation,
    toastList, currentFactory, topHeader, uploadList, failedList,
    invoiceFactory, openMenu, isInternal, isKwsFactory,
  ]);

  useEffect(() => {
    (async() => {
      let userDetails = {};
      if (!auth.isLoggedIn || !auth.getUserDetails()) {
        userDetails = await auth.authorize();
        if (userDetails) {
          setUser(userDetails);
        } else {
          console.log("user details not found!");
        }
      } else {
        userDetails = auth.getUserDetails()
        setUser(userDetails);
      }

      if (userDetails) {
        eventIdentify(
          userDetails.id,
          {
            email: userDetails.email,
            event_time: new Date().toISOString(),
            device_type: getUserDevice(),
            source: "tars_web"
          }
        );
      }

      // Run KWS check after auth is ready so getTarsAccessToken() has
      // access to auth.tokenResponse (needed to fetch a fresh TARS token).
      const factoryCode = isInvoiceManagementPage
        ? (invoiceFactory.item_code || currentFactory.item_code)
        : (currentFactory.item_code || invoiceFactory.item_code);
      if (factoryCode) {
        runKwsCheck(factoryCode);
      }
    })();
    if (currentFactory.item_code && (!workOrder || !workOrder.wo_id)) {
      setSearchFocus(true);
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-run KWS check when switching between invoice and production pages,
  // since the "active" factory changes (invoiceFactory ↔ currentFactory).
  // Skip initial mount — the mount effect above already handles it.
  // The effect closure captures invoiceFactory/currentFactory from the render
  // where isInvoiceManagementPage changes — these values are current at fire time.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    const factoryCode = isInvoiceManagementPage
      ? (invoiceFactory?.item_code || currentFactory?.item_code)
      : (currentFactory?.item_code || invoiceFactory?.item_code);
    if (factoryCode) {
      runKwsCheck(factoryCode);
    }
  }, [isInvoiceManagementPage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect guard: if on a production route and KWS check resolved to false, redirect
  // Internal users are exempt — Bouncer controls their access
  useEffect(() => {
    if (isInternal || isKwsFactory !== false) return;
    const isOnProductionRoute = PRODUCTION_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(r + "/"));
    if (isOnProductionRoute) {
      navigate("/dashboard/invoice-management", { replace: true });
    }
  }, [isKwsFactory, isInternal, location.pathname, navigate]);

  const logout = async () => {
    const logoutUrl = auth.logOut();
    if (logoutUrl) {
      window.location.assign(logoutUrl);
    } else {
      navigate(REDIRECT_URI);
    }
  }

  const updateFactory = (factory) => {
    localStorage.setItem('factory', JSON.stringify(factory));
    localStorage.removeItem('workOrder');
    localStorage.removeItem('seletedStation');
    setWorkOrder({});
    setCurrentFactory(factory);
    // Sync invoice factory so switching to invoice tab shows the same factory
    localStorage.setItem('invoiceFactory', JSON.stringify(factory));
    setInvoiceFactory(factory);
    setShowFactory(false);
    setSearchFocus(true);
    runKwsCheck(factory.item_code);
    rudderStackTrackEvent(
      "tars:on_factory_select",
      {
        user_id: `TARS${user.id}`,
        email: user.email,
        action: "select",
        factory: factory.item_code
      }
    );
  }

  useEffect(() => {
    let index = -1;
    uploadList.map((row, i) => {
      if (row.uploaded === row.totalUploaded) {
        index = i;
      }
      return row;
    })
    
    if (index > -1) {
      setTimeout(() => {
        setUploadList([...uploadList.slice(0,index), ...uploadList.slice(index+1)])
      }, 2000)
    }
    window.onbeforeunload = function() {
      if (uploadList.length > 0) {
        return "Are you sure you want to close? This will stop uploading the files in the background";
      }
    };
  }, [uploadList])

  const handleUploadClear = () => {
    setUploadList(list => {
      let temp = [...list];
      Object.keys(failedList).map((poCode) => {
        let index = temp.findIndex(x => x.poCode === poCode);
        let currentPO = {
          ...temp[index],
          totalUploaded: temp[index].totalUploaded - failedList[poCode].length
        }
        temp[index] = currentPO;
      })
      return temp;
    })
    setFailedList({});
  }

  if (cacheStale) {
    return (
      <div style={{ padding: 40, textAlign: "center", fontFamily: "sans-serif" }}>
        Signing you in again to refresh permissions…
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <UserContext.Provider value={contextData}>
      <PermissionsProvider permissions={bouncerPermissions}>
        <Header user={user} logout={logout} onFactoryClick={() => setShowFactory(true)}></Header>
        <div className={styles.contentWrapper}>
          <Sidebar focus={searchFocus} user={user}></Sidebar>
          <SidebarOverlay open={openMenu} onClose={() => setOpenMenu(false)} className={styles.sidebarOverlay} />
          <section className={styles.workspace}>
            <div>
              {
                user.email && (currentFactory && currentFactory.item_code || skipFactoryGate) ?
                <Routes>
                  {(isKwsFactory === true || isInternal) && (
                    <>
                      <Route path="beamsaw" element={<Beamsaw user={user} />} />
                      <Route path="qcsort" element={<QcSort user={user} />} />
                      <Route path="qccheck" element={<QcCheck user={user} />} />
                      <Route path="image-search" element={<ImageSearch user={user} />} />
                      <Route path="qccheck/:po_code" element={<PanelListView user={user} />} />
                    </>
                  )}
                  <Route
                    path="invoice-management/*"
                    element={<InvoiceManagementGate user={user} />}
                  />
                  <Route path="vendor-onboarding" element={<InviteVendor />} />
                  <Route path="vendor-profile" element={<VendorProfilePage />} />
                </Routes> : <></>
              }
              {!currentFactory.item_code && !skipFactoryGate && <div className={styles.errorFactory}>Kindly select factory to continue</div>}
            </div>
          </section>
        </div>
        {/* {showFactory && <Modal showClose={currentFactory.item_code != undefined} title={(currentFactory.item_code != undefined ? 'Change' : 'Select') + ' factory'} onClose={() => setShowFactory(false)}>
          <label htmlFor="changeFactory" className={styles.inputLabel}>Factory name</label>
          <SearchInput icon={false} style={{backgroundColor: 'white', border: '1px solid #CCCCCC'}} placeholder="Search factory name" onSearch={onFactorySearch} onItemSelection={updateFactory}></SearchInput>
        </Modal>} */}
        <Toast toastList={toastList} setToastList={setToastList}/>
        <UploadProgress rowList={uploadList} failedList={failedList} clearAll={handleUploadClear}></UploadProgress>
        <FactorySelector
          isInvoiceManagementPage={isInvoiceManagementPage}
          show={showFactory}
          user={user}
          dismissable={skipFactoryGate || currentFactory}
          onClose={() => setShowFactory(false)}
          onSelect={(factory) => {
            if (factory && factory.length > 1) {
              const _factory = {
                item_code: factory?.[0],
                item_name: factory?.[1],
                type: "FCT",
              };
              if (isInvoiceManagementPage) {
                localStorage.setItem('invoiceFactory', JSON.stringify(_factory));
                setInvoiceFactory(_factory);
                runKwsCheck(_factory.item_code);
                // Sync production factory so switching to production tabs
                // uses the same factory ("all" is invoice-only, skip it)
                if (_factory.item_code !== "all") {
                  localStorage.setItem('factory', JSON.stringify(_factory));
                  setCurrentFactory(_factory);
                }
              } else {
                updateFactory(_factory)
              }
            }
          }}
          />
      </PermissionsProvider>
      </UserContext.Provider>
      {showLoadingFull && 
      <div className={styles.loading}>
        <span className={styles.loadingText}></span>
      </div>}
    </div>
  );
}

export default Dashboard;
